import ExcelJS from "exceljs";
import { t } from "i18next";

import {
  getCurrencySymbol,
  parseBenchmarkTrainsData,
} from "./excelReportUtils";
import {
  createBenchmarkCIPHistorySheet,
  createBenchmarkCIPPredictRecommendSheet,
  createBenchmarkCIPSummarySheet,
  createBenchmarkCleanEffectivenessSheet,
  createBenchmarkComparisonWithThresholdSheet,
  createBenchmarkFoulingTypeSheet,
  createBenchmarkOptimizationSheet,
  createReplacementAlertSheet,
} from "./sheets";

import { saveExcelFile } from "../../../utils/Utils";

const exportExcelReport = async (
  trainsData,
  filename = null,
  plantsData = null,
  filterType = 3,
  units = null,
) => {
  try {
    if (!Array.isArray(trainsData)) {
      throw new Error("trainsData must be an array");
    }

    const currencySymbol =
      units && units.currency ? getCurrencySymbol(units.currency) : "$";
    const volumeUnit = units && units.volume ? units.volume : "m3";

    const parsedTrainsData = parseBenchmarkTrainsData(trainsData);

    if (parsedTrainsData.length === 0) {
      throw new Error("No valid trains data to export");
    }

    const workbook = new ExcelJS.Workbook();
    const hasPlantsData =
      plantsData && Array.isArray(plantsData) && plantsData.length > 0;
    const scopedData = hasPlantsData ? plantsData : parsedTrainsData;

    try {
      // filterType: 1 = hide lifetime estimates, 2 = show only lifetime estimates, 3 = include all sheets
      const includeSheets1To6 = filterType !== 2;
      const includeSheet7 = filterType !== 1;

      if (includeSheets1To6) {
        const sheet1 = workbook.addWorksheet(
          t("benchMark.sheetNames.optimizationResults"),
        );
        createBenchmarkOptimizationSheet(
          sheet1,
          parsedTrainsData,
          t,
          currencySymbol,
          volumeUnit,
        );
      }

      if (includeSheets1To6) {
        const sheet2 = workbook.addWorksheet(
          t("benchMark.sheetNames.cipPredictRecommend"),
        );
        createBenchmarkCIPPredictRecommendSheet(sheet2, parsedTrainsData, t);
      }

      if (includeSheets1To6) {
        const sheet3 = workbook.addWorksheet(
          t("excelSheetNames.predRecValuesVsThreshold"),
        );
        createBenchmarkComparisonWithThresholdSheet(sheet3, parsedTrainsData, t);
      }

      if (includeSheets1To6) {
        const sheet4 = workbook.addWorksheet(
          t("benchMark.sheetNames.cipHistory"),
        );
        createBenchmarkCIPHistorySheet(sheet4, scopedData, t);
      }

      if (includeSheets1To6) {
        const sheet5 = workbook.addWorksheet(
          t("benchMark.sheetNames.cipDetails"),
        );
        createBenchmarkCIPSummarySheet(sheet5, scopedData, t);
      }

      if (includeSheets1To6) {
        const sheet6 = workbook.addWorksheet(
          t("benchMark.sheetNames.cleanEffectiveness"),
        );
        createBenchmarkCleanEffectivenessSheet(sheet6, scopedData, t);
      }

      if (includeSheets1To6) {
        const sheet7 = workbook.addWorksheet(
          t("benchMark.sheetNames.foulingType"),
        );
        createBenchmarkFoulingTypeSheet(sheet7, scopedData, t);
      }

      if (includeSheet7) {
        const sheet8 = workbook.addWorksheet(
          t("benchMark.sheetNames.lifetimeEstimates"),
        );
        createReplacementAlertSheet(sheet8, scopedData, t);
      }
    } catch (sheetError) {
      throw new Error(`Failed to create sheets: ${sheetError.message}`);
    }

    const exportFilename = filename || t("benchMark.benchmarkReportName");
    await saveExcelFile(exportFilename, workbook);

    return { success: true, filename: exportFilename };
  } catch (error) {
    throw new Error(`Failed to export Excel Report: ${error.message}`);
  }
};

export default exportExcelReport;
